import type { SerializedStyles, Theme } from "@emotion/react";
import type { HTMLProps, ReactNode } from "react";

import * as styles from "./Radio.styles";

interface Props extends HTMLProps<HTMLInputElement> {
  /**
   * Adds custom styles to the label text styling
   */
  labelStyle?: (SerializedStyles | ((theme: Theme) => SerializedStyles))[] | SerializedStyles;
  /**
   * Content to render alongside the radio button
   *
   * @default undefined
   */
  children?: ReactNode;
  /**
   * Adds a data-e2e attribute to the label
   */
  "data-e2e"?: string;
}

/**
 * Documentation:
 * https://aviary.docs.fullscript.cloud/components/inputs/Radio
 *
 *
 * @deprecated Use `Radio` from `@fullscript/aviary-web` instead.
 */
const Radio = (props: Props) => {
  const { id, labelStyle, hidden, disabled, children, checked, ...rest } = props;

  const labelStyles = () => {
    return [styles.radioWrapper, hidden && styles.hidden, disabled && styles.disabled, labelStyle];
  };

  const radioCircleStyles = () => {
    return [styles.circleContainer.base, disabled && styles.circleContainer.disabled];
  };

  return (
    <label
      css={labelStyles()}
      data-e2e={props["data-e2e"] ? `${props["data-e2e"]}-label` : undefined}
    >
      <input
        id={id}
        type="radio"
        checked={checked}
        css={styles.radio}
        disabled={disabled}
        {...rest}
      />
      <div css={radioCircleStyles()}>
        <div css={styles.radioInner} />
      </div>
      <div css={styles.radioContent}>{children}</div>
    </label>
  );
};

export { Radio };
